import React, { useEffect, useMemo, useState } from "react";
import { Loader2, MessageCircle, Reply, Trash2, LogOut, Pencil, ThumbsUp } from "lucide-react";
import { useCommenter } from "../context/CommenterContext";
import { resolveAsset } from "../lib/api";
import {
  deleteOwnComment, editOwnComment, likeComment, listComments, postComment, startCommenterLogin,
} from "../lib/commenter";

/**
 * Comments box rendered under each blog post.
 *
 * Anyone can READ. Posting requires Google sign-in. Threaded by
 * `parent_id` but capped at one level deep (replies don't get their
 * own replies — backend enforces this too).
 */
export default function CommentsSection({ post }) {
  const { user, loading: userLoading, logout } = useCommenter();
  const [comments, setComments] = useState(null);
  const [error, setError] = useState("");
  const [replyTo, setReplyTo] = useState(null); // comment id or null
  const [body, setBody] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const postId = post?.id;
  const enabled = post?.comments_enabled !== false;

  useEffect(() => {
    if (!postId || !enabled) return;
    let alive = true;
    listComments(postId)
      .then((rows) => alive && setComments(rows))
      .catch(() => alive && setComments([]));
    return () => { alive = false; };
  }, [postId, enabled]);

  const tree = useMemo(() => {
    if (!comments) return [];
    // Two passes: collect top-level, then attach replies in insertion order.
    const byParent = new Map();
    const top = [];
    for (const c of comments) {
      if (c.parent_id) {
        if (!byParent.has(c.parent_id)) byParent.set(c.parent_id, []);
        byParent.get(c.parent_id).push(c);
      } else {
        top.push(c);
      }
    }
    return top.map((c) => ({ ...c, replies: byParent.get(c.id) || [] }));
  }, [comments]);

  if (!enabled) {
    return (
      <section
        className="mt-12 rounded-md border border-dashed border-black/15 p-6 text-center text-sm text-[var(--brand-body)]"
        data-testid="comments-disabled"
      >
        Comments are turned off for this post.
      </section>
    );
  }

  const submit = async (e) => {
    e.preventDefault();
    if (!body.trim() || submitting || !user) return;
    setSubmitting(true);
    setError("");
    try {
      const fresh = await postComment(postId, body.trim(), replyTo);
      setComments((prev) => [...(prev || []), fresh]);
      setBody("");
      setReplyTo(null);
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (err.response?.status === 401) {
        // Session expired — bounce to sign-in.
        startCommenterLogin();
        return;
      }
      setError(typeof detail === "string" ? detail : err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete your comment?")) return;
    try {
      await deleteOwnComment(id);
      setComments((prev) => (prev || []).map((c) => (c.id === id ? { ...c, deleted: true, body: "" } : c)));
    } catch (err) {
      setError(err.response?.data?.detail || err.message);
    }
  };

  const saveEdit = async (id, nextBody) => {
    const fresh = await editOwnComment(id, nextBody);
    setComments((prev) => (prev || []).map((c) => (c.id === id ? fresh : c)));
    return fresh;
  };

  const replyTarget = replyTo ? comments?.find((c) => c.id === replyTo) : null;

  return (
    <section
      className="mt-12 rounded-md border border-black/10 bg-white p-6 sm:p-8"
      data-testid="comments-section"
    >
      <div className="flex items-center gap-2">
        <MessageCircle size={16} strokeWidth={2.5} className="text-brand-accent" />
        <h2 className="font-display text-xl font-black tracking-tight">
          Comments {comments ? `· ${comments.filter((c) => !c.deleted).length}` : ""}
        </h2>
      </div>

      {/* Composer */}
      <div className="mt-5">
        {userLoading ? (
          <div className="flex items-center gap-2 text-sm text-[var(--brand-body)]">
            <Loader2 size={14} className="animate-spin" /> Checking sign-in…
          </div>
        ) : user ? (
          <form onSubmit={submit} data-testid="comment-form">
            <div className="flex items-start gap-3">
              <Avatar user={user} />
              <div className="min-w-0 flex-1">
                {replyTarget ? (
                  <p className="mb-1 label-kicker text-[var(--brand-body)]">
                    Replying to{" "}
                    <span className="font-bold text-[var(--brand-ink)]">
                      {replyTarget.author?.name || "comment"}
                    </span>{" "}
                    ·{" "}
                    <button
                      type="button"
                      onClick={() => setReplyTo(null)}
                      className="underline underline-offset-2 hover:text-[var(--brand-ink)]"
                    >
                      Cancel
                    </button>
                  </p>
                ) : null}
                <textarea
                  value={body}
                  onChange={(e) => setBody(e.target.value)}
                  maxLength={2000}
                  rows={3}
                  placeholder={`Say something, ${user.name?.split(" ")[0] || "fan"}…`}
                  data-testid="comment-textarea"
                  className="block w-full rounded-md border border-black/15 bg-white px-3 py-2 text-sm outline-none focus:border-[#0A0F17]"
                />
                <div className="mt-2 flex flex-wrap items-center justify-between gap-2">
                  <button
                    type="button"
                    onClick={logout}
                    data-testid="commenter-logout"
                    className="inline-flex items-center gap-1 label-kicker text-[var(--brand-body)] hover:text-[var(--brand-ink)]"
                  >
                    <LogOut size={11} /> Sign out
                  </button>
                  <div className="flex items-center gap-3">
                    <span className="text-xs text-[var(--brand-body)]">
                      {body.length}/2000
                    </span>
                    <button
                      type="submit"
                      disabled={!body.trim() || submitting}
                      data-testid="comment-submit"
                      className="inline-flex items-center gap-2 rounded-md bg-[#0A0F17] px-4 py-2 font-display text-sm font-bold text-white disabled:opacity-50"
                    >
                      {submitting ? <Loader2 size={14} className="animate-spin" /> : null}
                      {submitting ? "Posting…" : replyTo ? "Reply" : "Post comment"}
                    </button>
                  </div>
                </div>
                {error ? (
                  <p
                    className="mt-2 rounded-sm border-l-2 border-[#E63946] bg-[#E63946]/5 px-3 py-2 text-sm"
                    data-testid="comment-error"
                  >
                    {error}
                  </p>
                ) : null}
              </div>
            </div>
          </form>
        ) : (
          <button
            type="button"
            onClick={startCommenterLogin}
            data-testid="commenter-signin"
            className="inline-flex items-center gap-2 rounded-md border border-black/15 bg-white px-4 py-2 font-display text-sm font-bold text-[var(--brand-ink)] transition-colors hover:bg-[#0A0F17] hover:text-white"
          >
            Sign in with Google to comment
          </button>
        )}
      </div>

      {/* Comment list */}
      <div className="mt-8 space-y-6" data-testid="comment-list">
        {comments === null ? (
          <div className="flex items-center gap-2 text-sm text-[var(--brand-body)]">
            <Loader2 size={14} className="animate-spin" /> Loading…
          </div>
        ) : tree.length === 0 ? (
          <p className="text-sm text-[var(--brand-body)]">
            No comments yet. Be the first to say something.
          </p>
        ) : (
          tree.map((c) => (
            <CommentNode
              key={c.id}
              comment={c}
              currentUser={user}
              onReply={(id) => {
                setReplyTo(id);
                setTimeout(() => {
                  document.querySelector('[data-testid=comment-textarea]')?.focus();
                }, 50);
              }}
              onDelete={remove}
              onSaveEdit={saveEdit}
            />
          ))
        )}
      </div>
    </section>
  );
}

function CommentNode({ comment, currentUser, onReply, onDelete, onSaveEdit }) {
  return (
    <div data-testid={`comment-${comment.id}`}>
      <CommentBody c={comment} currentUser={currentUser} onReply={onReply} onDelete={onDelete} onSaveEdit={onSaveEdit} />
      {comment.replies?.length ? (
        <div className="mt-4 ml-12 space-y-4 border-l-2 border-black/5 pl-5">
          {comment.replies.map((r) => (
            <CommentBody
              key={r.id}
              c={r}
              currentUser={currentUser}
              onReply={() => onReply(comment.id)}
              onDelete={onDelete}
              onSaveEdit={onSaveEdit}
              isReply
            />
          ))}
        </div>
      ) : null}
    </div>
  );
}

function CommentBody({ c, currentUser, onReply, onDelete, onSaveEdit, isReply }) {
  const mine = currentUser?.user_id === c.author?.user_id;
  const when = formatWhen(c.created_at);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(c.body || "");
  const [saving, setSaving] = useState(false);
  const [editError, setEditError] = useState("");

  // Tick state so the "edit window remaining" countdown stays current
  // (and the Edit button vanishes the second the window closes) without
  // having to refetch the comments list.
  const canEdit = mine && !c.deleted && c.edit_window_expires_at && new Date(c.edit_window_expires_at) > new Date();
  const [, setTick] = useState(0);
  useEffect(() => {
    if (!canEdit) return;
    const id = setInterval(() => setTick((n) => n + 1), 15000);
    return () => clearInterval(id);
  }, [canEdit]);

  const minutesLeft = c.edit_window_expires_at
    ? Math.max(0, Math.ceil((new Date(c.edit_window_expires_at) - new Date()) / 60000))
    : 0;

  const cancelEdit = () => {
    setEditing(false);
    setDraft(c.body || "");
    setEditError("");
  };

  const save = async () => {
    const next = draft.trim();
    if (!next) return;
    setSaving(true);
    setEditError("");
    try {
      await onSaveEdit(c.id, next);
      setEditing(false);
    } catch (err) {
      setEditError(err.response?.data?.detail || err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="flex items-start gap-3">
      <Avatar user={c.author} deleted={c.deleted} />
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-baseline gap-2">
          <span className="font-display text-sm font-bold text-[var(--brand-ink)]">
            {c.deleted ? "—" : c.author?.name || "Anon"}
          </span>
          <span className="label-kicker text-[var(--brand-body)]">{when}</span>
          {c.edited_at && !c.deleted ? (
            <span
              className="label-kicker text-[var(--brand-body)]"
              title={`Edited ${new Date(c.edited_at).toLocaleString("en-GB")}`}
            >
              · edited
            </span>
          ) : null}
        </div>

        {editing ? (
          <div className="mt-2">
            <textarea
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              maxLength={2000}
              rows={3}
              autoFocus
              data-testid={`comment-${c.id}-edit-textarea`}
              className="block w-full rounded-md border border-black/15 bg-white px-3 py-2 text-sm outline-none focus:border-[#0A0F17]"
            />
            <div className="mt-2 flex flex-wrap items-center gap-2">
              <button
                type="button"
                onClick={save}
                disabled={!draft.trim() || saving}
                data-testid={`comment-${c.id}-edit-save`}
                className="inline-flex items-center gap-1 rounded-md bg-[#0A0F17] px-3 py-1.5 font-display text-xs font-bold text-white disabled:opacity-50"
              >
                {saving ? <Loader2 size={11} className="animate-spin" /> : null}
                {saving ? "Saving…" : "Save"}
              </button>
              <button
                type="button"
                onClick={cancelEdit}
                className="label-kicker text-[var(--brand-body)] underline-offset-2 hover:underline"
              >
                Cancel
              </button>
              <span className="ml-auto text-xs text-[var(--brand-body)]">
                {draft.length}/2000
              </span>
            </div>
            {editError ? (
              <p
                className="mt-2 rounded-sm border-l-2 border-[#E63946] bg-[#E63946]/5 px-3 py-2 text-sm"
                data-testid={`comment-${c.id}-edit-error`}
              >
                {editError}
              </p>
            ) : null}
          </div>
        ) : (
          <>
            <p className={`mt-1 whitespace-pre-wrap text-sm ${c.deleted ? "italic text-[var(--brand-body)]" : "text-[var(--brand-ink)]"}`}>
              {c.deleted ? "[deleted]" : c.body}
            </p>
            {!c.deleted && c.image_url ? (
              <div
                className="mt-2 w-full max-w-xs overflow-hidden rounded-md border border-black/10 bg-black/5"
                style={{ minHeight: 120 }}
              >
                <img
                  src={resolveAsset(c.image_url)}
                  alt="Attached lineup"
                  loading="lazy"
                  decoding="async"
                  className="max-h-80 w-full object-contain"
                  data-testid={`comment-${c.id}-image`}
                />
              </div>
            ) : null}
          </>
        )}

        {!c.deleted && !editing ? (
          <div className="mt-2 flex items-center gap-3">
            <LikeButton comment={c} />
            {!isReply ? (
              <button
                type="button"
                onClick={() => onReply(c.id)}
                data-testid={`comment-${c.id}-reply`}
                className="inline-flex items-center gap-1 label-kicker text-[var(--brand-body)] hover:text-[var(--brand-ink)]"
              >
                <Reply size={11} /> Reply
              </button>
            ) : null}
            {canEdit ? (
              <button
                type="button"
                onClick={() => setEditing(true)}
                data-testid={`comment-${c.id}-edit`}
                title={`Edit window: ${minutesLeft} min left`}
                className="inline-flex items-center gap-1 label-kicker text-[var(--brand-body)] hover:text-[var(--brand-ink)]"
              >
                <Pencil size={11} /> Edit
                <span className="ml-1 opacity-70">({minutesLeft}m left)</span>
              </button>
            ) : null}
            {mine ? (
              <button
                type="button"
                onClick={() => onDelete(c.id)}
                data-testid={`comment-${c.id}-delete`}
                className="inline-flex items-center gap-1 label-kicker text-[#E63946] hover:opacity-80"
              >
                <Trash2 size={11} /> Delete
              </button>
            ) : null}
          </div>
        ) : null}
      </div>
    </div>
  );
}

function Avatar({ user, deleted }) {
  if (deleted || !user?.picture) {
    return (
      <div className="h-10 w-10 shrink-0 rounded-full bg-black/10 flex items-center justify-center font-display text-xs font-black text-[var(--brand-body)]">
        {(user?.name || "?").slice(0, 1).toUpperCase()}
      </div>
    );
  }
  return (
    <img
      src={user.picture}
      alt=""
      className="h-10 w-10 shrink-0 rounded-full object-cover"
      referrerPolicy="no-referrer"
    />
  );
}

function LikeButton({ comment }) {
  const storageKey = `last-liked-${comment.id}`;
  // Initial "have I liked this on this browser?" state is read from
  // localStorage so the button stays pressed across reloads. The server
  // is still the source of truth for the count.
  const [liked, setLiked] = useState(() => {
    try { return localStorage.getItem(storageKey) === "1"; } catch { return false; }
  });
  const [count, setCount] = useState(Number(comment.likes || 0));
  const [busy, setBusy] = useState(false);

  const onClick = async () => {
    if (liked || busy) return;
    setBusy(true);
    // Optimistic update — instant feedback, server confirms count.
    setLiked(true);
    setCount((n) => n + 1);
    try { localStorage.setItem(storageKey, "1"); } catch { /* ignore */ }
    try {
      const fresh = await likeComment(comment.id);
      if (typeof fresh?.likes === "number") setCount(fresh.likes);
    } catch (_err) {
      // Roll back UX on failure so the button can be re-tried.
      setLiked(false);
      setCount((n) => Math.max(0, n - 1));
      try { localStorage.removeItem(storageKey); } catch { /* ignore */ }
    } finally {
      setBusy(false);
    }
  };

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={liked || busy}
      data-testid={`comment-${comment.id}-like`}
      aria-pressed={liked}
      aria-label={liked ? `Liked. ${count} likes` : `Like this comment. ${count} likes`}
      title={liked ? "You liked this" : "Like this comment"}
      className={`inline-flex items-center gap-1 label-kicker transition-colors ${
        liked
          ? "text-[#6CABDD]"
          : "text-[var(--brand-body)] hover:text-[var(--brand-ink)] disabled:opacity-60"
      }`}
    >
      <ThumbsUp size={11} strokeWidth={liked ? 2.5 : 2} fill={liked ? "currentColor" : "none"} />
      {count > 0 ? <span>{count}</span> : null}
    </button>
  );
}

function formatWhen(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const now = new Date();
  const diff = (now - d) / 1000;
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 604800) return `${Math.floor(diff / 86400)}d ago`;
  return d.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
}
